import React, { useState } from "react";
import { List, Avatar, Space, Empty, Card, Button, Typography, Image } from "antd";
import { UserOutlined, PlusOutlined, FilePdfOutlined, FileImageOutlined, DownloadOutlined, PaperClipOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import type { IncidenceComment } from "../../../types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { AddCommentModal } from "./AddCommentModal";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { Link } = Typography;

interface IncidenceCommentsProps {
  incidenceId: number;
}

/**
 * IncidenceComments Component
 * Displays a list of comments for an incidence and provides a form to add new comments
 * Features:
 * - Lists comments with avatar, author, date, and text
 * - Orders comments chronologically (oldest first)
 * - Form to add new comments with validation
 * - Auto-refresh after adding a comment
 */
export const IncidenceComments: React.FC<IncidenceCommentsProps> = ({
  incidenceId,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);

  // Fetch comments for this incidence
  const queryResult = useList<IncidenceComment>({
    resource: "incidence-comments",
    filters: [
      {
        field: "incidence_id",  // Backend expects incidence_id as query param
        operator: "eq",
        value: incidenceId,
      },
    ],
    sorters: [
      {
        field: "created",
        order: "desc", // Most recent first (newest to oldest)
      },
    ],
    pagination: {
      current: 1,
      pageSize: 100, // Load up to 100 comments
    },
    queryOptions: {
      // Add a query key to make invalidation more specific
      queryKey: ["incidence-comments", "list", { incidence_id: incidenceId }],
    },
  });

  // Get comments from the correct path in the query result
  const comments = queryResult.result?.data || [];
  const isLoading = queryResult.query.isLoading;
  
  // Function to refresh comments list
  const refreshComments = () => {
    queryResult.query.refetch();
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = dayjs(dateString);
    const now = dayjs();
    const diffInHours = now.diff(date, "hour");

    // Show relative time for recent comments (less than 24 hours)
    if (diffInHours < 24) {
      return date.fromNow();
    }

    // Show full date for older comments
    return date.format("DD/MM/YYYY HH:mm");
  };

  // Check if file is an image
  const isImage = (filename: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  // Check if file is a PDF
  const isPDF = (filename: string): boolean => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  // Render attachment preview/link
  const renderAttachment = (comment: IncidenceComment) => {
    if (!comment.attachment_url || !comment.attachment_name) {
      return null;
    }

    const filename = comment.attachment_name;

    if (isImage(filename)) {
      return (
        <div style={{ marginTop: '12px' }}>
          <Image
            src={comment.attachment_url}
            alt={filename}
            style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px' }}
            preview={{
              mask: <div><FileImageOutlined /> Ver imagen</div>
            }}
          />
          <div style={{ marginTop: '4px' }}>
            <Link href={comment.attachment_url} target="_blank">
              <DownloadOutlined /> {filename}
            </Link>
          </div>
        </div>
      );
    }

    if (isPDF(filename)) {
      return (
        <div style={{ marginTop: '12px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
          <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f', marginRight: '8px' }} />
          <Link href={comment.attachment_url} target="_blank">
            <DownloadOutlined /> {filename}
          </Link>
        </div>
      );
    }

    // Generic file
    return (
      <div style={{ marginTop: '12px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
        <PaperClipOutlined style={{ fontSize: '20px', marginRight: '8px' }} />
        <Link href={comment.attachment_url} target="_blank">
          <DownloadOutlined /> {filename}
        </Link>
      </div>
    );
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {/* Comments List */}
      <Card
        title={t("incidence.comments_list", "Comentarios")}
        extra={
          <Space>
            <span>
              {t("common.total", "Total")}: {comments.length}
            </span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              {t("incidence.add_comment", "Añadir Comentario")}
            </Button>
          </Space>
        }
      >
        {comments.length === 0 ? (
          <Empty
            description={t(
              "incidence.no_comments",
              "No hay comentarios todavía. ¡Sé el primero en comentar!"
            )}
          />
        ) : (
          <List
            dataSource={comments}
            loading={isLoading}
            itemLayout="horizontal"
            renderItem={(comment) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<UserOutlined />}
                      style={{ backgroundColor: "#1890ff" }}
                    />
                  }
                  title={
                    <Space>
                      <strong>{comment.user_name || t("common.unknown_user", "Usuario desconocido")}</strong>
                      <span style={{ color: "#999", fontSize: "12px" }}>
                        {formatDate(comment.created)}
                      </span>
                    </Space>
                  }
                  description={
                    <div>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          marginTop: "8px",
                          // Romper palabras larguísimas (URLs sin
                          // espacios, hashes, identificadores…) para
                          // que el texto no se salga del bocadillo en
                          // pantallas estrechas o cuando el detalle
                          // se ve en columnas.
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {comment.message}
                      </div>
                      {renderAttachment(comment)}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Add Comment Modal */}
      <AddCommentModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        incidenceId={incidenceId}
        onSuccess={refreshComments}
      />
    </Space>
  );
};
